import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Tooltip,
  TextField,
  Alert,
  LinearProgress,
  Stack,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Swal from "sweetalert2";

import useFeedbackModal from "../../../hooks/useFeedbackModal";
import { colors } from "../../../theme/colors";
import { getMisNotasNuevo, deleteNotaAutorizacionNuevo } from "../../../services/notasDeAutorizacionNuevo.service";
import ModalCargarNotaNuevo from "./ModalCargarNotaNuevo";
import EditarNotaAutorizacion from "../EditarNotaAutorizacion";

// ---------------------------------------------------------------------------
// HELPERS & CONFIG
// ---------------------------------------------------------------------------

const formatCoordName = (referente) => {
  if (!referente) return '—';
  const partes = referente.split(',').map((p) => p.trim());
  if (partes.length === 2) {
    return `${partes[1]} ${partes[0]}`;
  }
  return referente;
};

const formatDate = (isoOrStr) => {
  if (!isoOrStr) return '—';
  const d = new Date(isoOrStr);
  if (isNaN(d.getTime())) return isoOrStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const ESTADO_CONFIG = {
  PCARGA: {
    label: 'Pendiente Suba',
    badgeText: 'Pendiente Suba',
    color: 'warning',
    dotColor: colors.CBA_Orange || '#F0AD4E',
    badgeBg: '#FFFBEB',
    badgeBorder: '#FDE68A',
    badgeColor: '#B45309',
  },
  PREV: {
    label: 'Pendientes Revisión',
    badgeText: 'Pendientes Revisión',
    color: 'info',
    dotColor: colors.CBA_Blue || '#009ada',
    badgeBg: '#EFF6FF',
    badgeBorder: '#BFDBFE',
    badgeColor: '#1D4ED8',
  },
  PEND: {
    label: 'Pendientes Revisión',
    badgeText: 'Pendientes Revisión',
    color: 'info',
    dotColor: colors.CBA_Blue || '#009ada',
    badgeBg: '#EFF6FF',
    badgeBorder: '#BFDBFE',
    badgeColor: '#1D4ED8',
  },
  AUT: {
    label: 'Autorizada',
    badgeText: 'Autorizada',
    color: 'success',
    dotColor: colors.CBA_Green || '#5CB85C',
    badgeBg: '#F0FDF4',
    badgeBorder: '#BBF7D0',
    badgeColor: '#15803D',
  },
  REC: {
    label: 'Rechazada',
    badgeText: 'Rechazada',
    color: 'error',
    dotColor: colors.CBA_Error || '#E2464C',
    badgeBg: '#FEF2F2',
    badgeBorder: '#FECACA',
    badgeColor: '#B91C1C',
  },
};

// ---------------------------------------------------------------------------
// ADAPTER DATA
// ---------------------------------------------------------------------------

const adaptarNotaAlFrontend = (backendNota) => {
  const cambiosEstados = backendNota.cambios_estados || [];

  let rawEstado = null;
  if (cambiosEstados.length > 0) {
    const activo = cambiosEstados.find((ce) => ce.fecha_hora_hasta === null);
    rawEstado = activo ? activo.estado_nota_autorizacion : cambiosEstados[cambiosEstados.length - 1].estado_nota_autorizacion;
  } else {
    rawEstado = backendNota.estado_nota_autorizacion_cod;
  }

  const mapEstado = (cod) => {
    if (cod === 'PCARG' || cod === 'PCARGA') return 'PCARGA';
    if (cod === 'PEND') return 'PREV';
    if (cod === 'EC' || cod === 'AUT') return 'AUT';
    if (cod === 'REC') return 'REC';
    return cod || 'PCARGA';
  };

  const getEstadoFecha = (cod) => {
    const ce = cambiosEstados.find((e) => e.estado_nota_autorizacion === cod);
    return ce ? ce.fecha_hora_desde : null;
  };

  const fechaGeneracion = getEstadoFecha('PCARGA') || getEstadoFecha('PCARG') || backendNota.fecha_hora_desde || backendNota.createdAt || null;
  const fechaSuba = getEstadoFecha('PEND') || getEstadoFecha('PREV') || backendNota.fecha_suba || null;
  const fechaAutorizacion = getEstadoFecha('AUT') || getEstadoFecha('EC') || null;
  const fechaRechazo = getEstadoFecha('REC') || null;
  const estado = mapEstado(rawEstado);
  const cambioRec = cambiosEstados.find((e) => e.estado_nota_autorizacion === 'REC');
  const motivoRechazo = estado === 'REC' ? (cambioRec?.comentario || backendNota.motivo_rechazo || null) : null;

  const coordinador = backendNota.detalle_coordinador || {};
  const referente = backendNota.detalle_usuario_referente?.detalle_persona || {};

  const referenteNombre = `${referente.nombre || ''} ${referente.apellido || ''}`.trim() || 'Sin asignar';
  const coordNombre = coordinador.nombre && coordinador.apellido
    ? `${coordinador.nombre} ${coordinador.apellido}`.trim()
    : formatCoordName(referenteNombre);

  return {
    id: backendNota.id,
    estado,
    fecha: fechaGeneracion,
    fechaCarga: fechaGeneracion,
    fechaSuba,
    fechaAutorizacion,
    fechaRechazo,
    referente: referenteNombre,
    coordNombre,
    area: backendNota.detalle_area?.nombre || backendNota.area_cod,
    motivoRechazo,
    ruta_archivo_local: backendNota.ruta_archivo_local,
    datos: {
      area: backendNota.detalle_area?.nombre || backendNota.area_cod,
      cursos: backendNota.cursos || [],
      coordinador: {
        cuil: coordinador.cuil,
        nombre: coordinador.nombre || '',
        apellido: coordinador.apellido || '',
        mail: coordinador.mail || coordinador.email || '',
        celular: coordinador.celular || coordinador.telefono || '',
      },
    },
    raw: backendNota,
  };
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const MisNotasAutorizacionNuevo = () => {
  const { showDelete } = useFeedbackModal();
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterEstado, setFilterEstado] = useState('TODAS');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterArea, setFilterArea] = useState('Todas las áreas');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));

  // Ordenamiento
  const [sortByField, setSortByField] = useState('fecha');
  const [sortDesc, setSortDesc] = useState(true);

  // Modales y acciones
  const [selectedNotaId, setSelectedNotaId] = useState(null);
  const [isReemplazoModal, setIsReemplazoModal] = useState(false);
  const [editingNotaId, setEditingNotaId] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchMisNotas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMisNotasNuevo();
      setAutorizaciones(response || []);
    } catch (error) {
      console.error('Error al obtener las notas de autorización:', error);
      setAlert({
        open: true,
        message: 'Error al cargar las notas de autorización.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMisNotas();
  }, [fetchMisNotas]);

  const handleRefreshNotas = () => {
    setAlert({
      open: true,
      message: 'Nota de autorización actualizada correctamente. Actualizando lista...',
      severity: 'success',
    });
    fetchMisNotas();
  };

  const handleDeleteNota = async (id) => {
    const isConfirmed = await showDelete({
      title: 'Eliminar nota de autorización',
      detail: {
        text: '¿Está seguro de que quiere eliminar la',
        highlight: `Nota #${id}`,
        suffix: '?',
        subtext: 'Esta acción no se puede deshacer.',
      },
      confirmText: 'ELIMINAR',
      cancelText: 'CANCELAR',
    });

    if (isConfirmed) {
      try {
        await deleteNotaAutorizacionNuevo(id);
        setAlert({
          open: true,
          message: `Nota ID ${id} eliminada correctamente.`,
          severity: 'success',
        });
        fetchMisNotas();
      } catch (error) {
        console.error('Error al eliminar la nota de autorización:', error);
        setAlert({
          open: true,
          message: error.message || 'Error al eliminar la nota de autorización.',
          severity: 'error',
        });
      }
    }
  };

  const handleVerPdf = (ruta) => {
    if (ruta) {
      const cleanRuta = ruta.startsWith('/') ? ruta.slice(1) : ruta;
      const url =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:4000'
          : window.location.origin;

      window.open(`${url}/${cleanRuta}`, '_blank');
    }
  };

  const notas = useMemo(
    () => autorizaciones.map(adaptarNotaAlFrontend),
    [autorizaciones]
  );

  const areasDisponibles = useMemo(() => {
    const areas = Array.from(new Set(notas.map((n) => n.area).filter(Boolean)));
    return areas.sort((a, b) => a.localeCompare(b));
  }, [notas]);

  const currentYear = new Date().getFullYear();
  const yearsDisponibles = useMemo(() => {
    const years = new Set([String(currentYear)]);
    notas.forEach((n) => {
      if (n.fecha) {
        const d = new Date(n.fecha);
        if (!isNaN(d.getFullYear())) {
          years.add(String(d.getFullYear()));
        }
      }
    });
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [notas, currentYear]);

  const counts = useMemo(
    () => ({
      TODAS: notas.length,
      PCARGA: notas.filter((n) => n.estado === 'PCARGA').length,
      PREV: notas.filter((n) => n.estado === 'PREV' || n.estado === 'PEND').length,
      AUT: notas.filter((n) => n.estado === 'AUT').length,
      REC: notas.filter((n) => n.estado === 'REC').length,
    }),
    [notas]
  );

  const parseDateToMillis = (val) => {
    if (!val) return 0;
    const t = new Date(val).getTime();
    return isNaN(t) ? 0 : t;
  };

  const notasFiltradas = useMemo(() => {
    return notas
      .filter((n) => {
        // Filtro de Estado
        if (filterEstado !== 'TODAS') {
          if (filterEstado === 'PREV') {
            if (n.estado !== 'PREV' && n.estado !== 'PEND') return false;
          } else if (n.estado !== filterEstado) {
            return false;
          }
        }

        // Filtro de Área
        if (filterArea !== 'Todas las áreas' && n.area !== filterArea) {
          return false;
        }

        // Filtro de Año de Generación
        if (filterYear !== 'TODOS') {
          if (!n.fecha) return false;
          const d = new Date(n.fecha);
          if (String(d.getFullYear()) !== filterYear) return false;
        }

        // Buscador unificado: ID Nota, Referente o Curso
        if (searchQuery.trim() !== '') {
          const query = searchQuery.trim().toLowerCase();
          const matchId = n.id.toString().includes(query);
          const matchReferente = (n.referente || '').toLowerCase().includes(query);
          const matchCurso = (n.datos?.cursos || []).some((c) =>
            (c.nombre || '').toLowerCase().includes(query)
          );
          if (!matchId && !matchReferente && !matchCurso) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const valA = a[sortByField];
        const valB = b[sortByField];

        const dateA = parseDateToMillis(valA);
        const dateB = parseDateToMillis(valB);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const diff = dateA - dateB;
        return sortDesc ? -diff : diff;
      });
  }, [notas, filterEstado, filterArea, filterYear, searchQuery, sortByField, sortDesc]);

  const chips = [
    { key: 'TODAS', label: `Todas (${counts.TODAS})` },
    { key: 'PCARGA', label: `Pendientes Suba (${counts.PCARGA})` },
    { key: 'PREV', label: `Pendientes Revisión (${counts.PREV})` },
    { key: 'AUT', label: `Autorizadas (${counts.AUT})` },
    { key: 'REC', label: `Rechazadas (${counts.REC})` },
  ];

  if (editingNotaId) {
    return (
      <EditarNotaAutorizacion
        notaId={editingNotaId}
        onCancel={() => setEditingNotaId(null)}
        onSuccess={() => {
          setEditingNotaId(null);
          setAlert({
            open: true,
            message: 'Nota de autorización actualizada correctamente.',
            severity: 'success',
          });
          fetchMisNotas();
        }}
      />
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Alerta de notificación */}
      {alert.open && (
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Encabezado */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          color="text.primary"
          gutterBottom
        >
          Mis Notas de Autorización
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestione sus notas de autorización, suba los documentos firmados o realice correcciones.
        </Typography>
      </Box>

      {/* Fila de Filtros Superiores (Buscador, Área, Año de Generación) */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          bgcolor: 'common.white',
          borderColor: 'grey.300',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2.5,
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Buscador unificado: ID Nota, Referente o Curso */}
        <TextField
          size="small"
          placeholder="Buscar por ID Nota, Referente o Curso"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: { xs: 0, md: 300 },
            width: { xs: '100%', md: 'auto' },
            bgcolor: 'common.white',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        {/* Área */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Typography variant="body2" fontWeight={550} color="text.primary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Área:
          </Typography>
          <Select
            size="small"
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            sx={{
              flex: { xs: 1, md: 'initial' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: 0, md: 220 },
              bgcolor: 'common.white',
              borderRadius: 2,
            }}
          >
            <MenuItem value="Todas las áreas">Todas las áreas</MenuItem>
            {areasDisponibles.map((area) => (
              <MenuItem key={area} value={area}>
                {area}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Año de Generación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Typography variant="body2" fontWeight={550} color="text.primary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Año de Generación:
          </Typography>
          <Select
            size="small"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            sx={{
              flex: { xs: 1, md: 'initial' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: 0, md: 160 },
              bgcolor: 'common.white',
              borderRadius: 2,
            }}
          >
            <MenuItem value="TODOS">Todos los años</MenuItem>
            {yearsDisponibles.map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Paper>

      {/* Chips de Estado + Ordenamiento */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Chips de estado */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map(({ key, label }) => {
            const active = filterEstado === key;
            return (
              <Chip
                key={key}
                label={label}
                clickable
                onClick={() => setFilterEstado(key)}
                sx={{
                  borderRadius: '20px',
                  px: 0.5,
                  py: 2,
                  fontWeight: active ? 600 : 500,
                  fontSize: '0.85rem',
                  bgcolor: active ? 'primary.main' : 'common.white',
                  color: active ? 'common.white' : 'text.primary',
                  border: '1px solid',
                  borderColor: active ? 'primary.main' : 'grey.300',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: active ? 'primary.dark' : 'grey.100',
                    borderColor: 'primary.main',
                    color: active ? 'common.white' : 'primary.main',
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Control de Orden */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
              Ordenar por:
            </Typography>
            <Select
              variant="standard"
              disableUnderline
              size="small"
              value={sortByField}
              onChange={(e) => setSortByField(e.target.value)}
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.primary',
                cursor: 'pointer',
              }}
            >
              <MenuItem value="fecha">Fecha de Generación</MenuItem>
              <MenuItem value="fechaSuba">Fecha de Suba</MenuItem>
              <MenuItem value="fechaAutorizacion">Fecha de Autorización</MenuItem>
              <MenuItem value="fechaRechazo">Fecha de Rechazo</MenuItem>
            </Select>
          </Box>

          <Tooltip
            title={
              sortDesc
                ? 'Orden descendente (más recientes primero). Clic para cambiar a ascendente.'
                : 'Orden ascendente (más antiguas primero). Clic para cambiar a descendente.'
            }
            arrow
          >
            <IconButton
              onClick={() => setSortDesc(!sortDesc)}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                bgcolor: 'common.white',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <SwapVertIcon
                sx={{
                  fontSize: 20,
                  transform: sortDesc ? 'none' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease',
                  color: 'text.secondary',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Grid de Tarjetas */}
      {loading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            Cargando notas de autorización...
          </Typography>
        </Box>
      ) : notasFiltradas.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 3,
            borderColor: 'grey.300',
            bgcolor: 'common.white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
          }}
        >
          <AssignmentIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5, opacity: 0.5 }} />
          <Typography variant="body1" color="text.secondary">
            No hay notas en esta categoría.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {notasFiltradas.map((nota) => {
            const cfg = ESTADO_CONFIG[nota.estado] || ESTADO_CONFIG.PCARGA;
            return (
              <Grid item xs={12} sm={6} md={4} key={nota.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: 'grey.200',
                    bgcolor: 'common.white',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 14px rgba(0,0,0,0.09)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, pb: 1.5, flex: 1 }}>
                    {/* Header de la tarjeta: Área y Badge con Dot */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {nota.area}
                      </Typography>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.8,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 4,
                          bgcolor: cfg.badgeBg,
                          border: '1px solid',
                          borderColor: cfg.badgeBorder,
                          color: cfg.badgeColor,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: cfg.dotColor,
                          }}
                        />
                        {cfg.badgeText}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5, borderColor: 'grey.300' }} />

                    {/* Metadatos con Iconos */}
                    <Stack spacing={1.2} sx={{ fontSize: '0.85rem' }}>
                      {/* Referente */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          Referente: <span style={{ color: "#000", fontWeight: 550 }}>{nota.referente}</span>
                        </Typography>
                      </Box>

                      {/* Cursos + Coordinador */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {nota.datos.cursos?.length || 0} curso(s) · Coord: <span style={{ color: "#000", fontWeight: 550 }}>{nota.coordNombre}</span>
                        </Typography>
                      </Box>

                      {/* Nota ID */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          Nota ID: <span style={{ color: "#000", fontWeight: 550 }}>{nota.id}</span>
                        </Typography>
                      </Box>

                      {/* Fecha de Generación */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          Fecha de Generación: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fecha)}</span>
                        </Typography>
                      </Box>

                      {/* Fecha de Suba */}
                      {(nota.estado === 'PREV' || nota.estado === 'PEND' || nota.estado === 'AUT' || nota.estado === 'REC') && nota.fechaSuba && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CloudUploadIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Suba: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaSuba)}</span>
                          </Typography>
                        </Box>
                      )}

                      {/* Fecha de Autorización */}
                      {nota.estado === 'AUT' && nota.fechaAutorizacion && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Autorización: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaAutorizacion)}</span>
                          </Typography>
                        </Box>
                      )}

                      {/* Fecha de Rechazo */}
                      {nota.estado === 'REC' && nota.fechaRechazo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CancelIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            Fecha de Rechazo: <span style={{ color: "#000", fontWeight: 550 }}>{formatDate(nota.fechaRechazo)}</span>
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Motivo de rechazo (solo si la nota está rechazada) */}
                    {nota.estado === 'REC' && nota.motivoRechazo && (
                      <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon fontSize="small" />}
                        sx={{
                          mt: 2,
                          py: 0.5,
                          px: 1.5,
                          fontSize: '0.78rem',
                          borderRadius: 2,
                          bgcolor: 'rgba(226, 70, 76, 0.08)',
                          color: 'error.dark',
                          border: '1px solid',
                          borderColor: 'rgba(226, 70, 76, 0.25)',
                        }}
                      >
                        {nota.motivoRechazo}
                      </Alert>
                    )}
                  </CardContent>

                  {/* Acciones de la Tarjeta */}
                  <CardActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
                    {nota.estado === 'PCARGA' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, width: '100%' }}>
                        {nota.ruta_archivo_local ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleVerPdf(nota.ruta_archivo_local)}
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              py: 0.8,
                              borderRadius: 2,
                            }}
                          >
                            Ver PDF
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              setIsReemplazoModal(false);
                              setSelectedNotaId(nota.id);
                            }}
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              py: 0.8,
                              borderRadius: 2,
                            }}
                          >
                            Subir PDF
                          </Button>
                        )}

                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setEditingNotaId(nota.id)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                            borderColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'grey.50',
                            },
                          }}
                        >
                          Editar
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteNota(nota.id)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    ) : nota.estado === 'REC' ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: nota.ruta_archivo_local ? 'repeat(3, 1fr)' : '1fr 1fr',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        {nota.ruta_archivo_local && (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleVerPdf(nota.ruta_archivo_local)}
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              py: 0.8,
                              borderRadius: 2,
                              borderColor: 'grey.300',
                              color: 'text.primary',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'grey.50',
                              },
                            }}
                          >
                            Ver PDF
                          </Button>
                        )}

                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setEditingNotaId(nota.id)}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                            borderColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'grey.50',
                            },
                          }}
                        >
                          Editar
                        </Button>

                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            setIsReemplazoModal(true);
                            setSelectedNotaId(nota.id);
                          }}
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.8,
                            borderRadius: 2,
                          }}
                        >
                          {nota.ruta_archivo_local ? 'Reemplazar' : 'Reemplazar PDF'}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant={nota.estado === 'PREV' || nota.estado === 'PEND' ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        fullWidth
                        disabled={!nota.ruta_archivo_local}
                        startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                        onClick={() => handleVerPdf(nota.ruta_archivo_local)}
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          py: 0.85,
                          borderRadius: 2,
                        }}
                      >
                        {nota.ruta_archivo_local ? 'Ver PDF' : 'Sin archivo adjunto'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Modal para Cargar / Reemplazar PDF */}
      <ModalCargarNotaNuevo
        open={!!selectedNotaId}
        notaId={selectedNotaId}
        isReemplazo={isReemplazoModal}
        userEmail={
          (() => {
            const currentNota = notas.find((n) => n.id === selectedNotaId);
            return (
              currentNota?.raw?.detalle_usuario_referente?.detalle_persona?.mail ||
              currentNota?.datos?.coordinador?.mail ||
              ""
            );
          })()
        }
        onClose={() => {
          setSelectedNotaId(null);
          setIsReemplazoModal(false);
        }}
        onSuccess={() => {
          setSelectedNotaId(null);
          setIsReemplazoModal(false);
          handleRefreshNotas();
        }}
      />
    </Container>
  );
};

export default MisNotasAutorizacionNuevo;
